import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLeadForm } from '../hooks/useLeadForm';
import { getCountryCodesForSelect, getDialCodeFromCountry } from '../utils/countryMapping';

export const Contact = () => {
  const { geo, isLoading, error, submitForm, clearError } = useLeadForm();
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', country: 'US', contactNumber: '', subject: '', message: ''
  });
  const countryCodesOptions = getCountryCodesForSelect();

  useEffect(() => {
    if (!geo?.data?.countryCode) return;
    const cc = geo.data.countryCode;
    const dc = getDialCodeFromCountry(cc);
    setFormData(prev => ({ ...prev, country: cc, countryCode: `+${dc}` }));
  }, [geo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    // Merge subject into message for the CRM
    const payload = { ...formData, message: formData.subject ? `[${formData.subject}] ${formData.message}` : formData.message };
    await submitForm(payload);
  };

  const contactCards = [
    { icon: Mail, t: "Email Us", lines: ["info@IPProtectionOffice.com"] },
    { icon: Phone, t: "Call Us", lines: ["+1 (844) 382-8963"] },
    { icon: MapPin, t: "Visit Us", lines: ["16830 Ventura Blvd", "Encino, CA 91436"] },
    { icon: Clock, t: "Office Hours", lines: ["Monday - Friday: 9:00 AM - 6:00 PM PST", "Saturday - Sunday: Closed"] }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero */}
      <section className="relative bg-[#383D3B] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1739285452618-0b7b3d04f953?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGRlYWx8ZW58MHx8fHwxNzc2Mjg0ODk2fDA&ixlib=rb-4.1.0&q=85" alt="Business professionals greeting" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#383D3B]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl md:text-2xl text-gray-300">Questions about our services? Our team is ready to help you find the right path forward.</p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-8">Reach Our Team</h2>
              <p className="text-lg text-gray-600 mb-8">Whether you have a quick question or need detailed guidance on a complex filing, we are here to assist.</p>
              <div className="hidden md:block space-y-6">
                {contactCards.map((c, i) => (
                  <Card key={i} className="p-6 flex items-start space-x-4 hover:shadow-lg transition-shadow">
                    <div className="flex-shrink-0"><div className="w-12 h-12 bg-[#e8edc7] rounded-lg flex items-center justify-center"><c.icon className="h-6 w-6 text-[#99AA38]" /></div></div>
                    <div><h3 className="font-semibold text-[#383D3B] mb-1">{c.t}</h3>{c.lines.map((l, j) => <p key={j} className="text-gray-600">{l}</p>)}</div>
                  </Card>
                ))}
              </div>
              <MobileCarousel>
                {contactCards.map((c, i) => (
                  <Card key={i} className="p-6 flex items-start space-x-4 shadow-lg">
                    <div className="flex-shrink-0"><div className="w-12 h-12 bg-[#e8edc7] rounded-lg flex items-center justify-center"><c.icon className="h-6 w-6 text-[#99AA38]" /></div></div>
                    <div><h3 className="font-semibold text-[#383D3B] mb-1">{c.t}</h3>{c.lines.map((l, j) => <p key={j} className="text-gray-600">{l}</p>)}</div>
                  </Card>
                ))}
              </MobileCarousel>
            </div>

            <div className="order-1 lg:order-2">
              <Card className="p-4 sm:p-6 md:p-8 shadow-lg">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#383D3B] mb-6">Send a Message</h2>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2" placeholder="Your full name" disabled={isLoading} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" placeholder="your.email@example.com" disabled={isLoading} />
                  </div>
                  {/* {geo?.data?.countryName && (
                    <div>
                      <Label>Country</Label>
                      <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-gray-900 font-medium">{geo.data.countryName}</p>
                      </div>
                    </div>
                  )} */}
                  <div>
                    <Label htmlFor="contact">Contact Number *</Label>
                    <div className="flex gap-2 mt-2">
                      <Select value={formData.countryCode} onValueChange={(value) => { const sc = countryCodesOptions.find(c => c.code === value); setFormData({ ...formData, countryCode: value, country: sc?.countryCode || 'US' }); }} disabled={isLoading}>
                        <SelectTrigger className="w-24 sm:w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{countryCodesOptions.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input id="contact" type="tel" required value={formData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 10) setFormData({ ...formData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={10} disabled={isLoading} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="mt-2" placeholder="What is this regarding?" disabled={isLoading} />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea id="message" required value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="mt-2" placeholder="Provide details about your inquiry..." rows={5} disabled={isLoading} />
                  </div>
                  <Button type="submit" className="w-full bg-[#99AA38] hover:bg-[#889930] py-5 md:py-6 text-base md:text-lg font-semibold" disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
